import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  RefreshCw, Eye, ChevronRight, CheckCircle, Clock, XCircle, 
  AlertCircle, Layers, X, Image, Tag, Package, ArrowLeft, Lock,
  Search, ArrowUpDown, Filter, ChevronLeft
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';
import MaskAnnotator from '@/components/MaskAnnotationDialog';

// Record Detail Modal with MaskAnnotator
function RecordDetailModal({ isOpen, onClose, recordId, onLockAcquired }) {
  const { authenticatedFetch, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordData, setRecordData] = useState(null);
  const [currentLabelIndex, setCurrentLabelIndex] = useState(0);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [lockId, setLockId] = useState(null);
  const [maskAnnotatorKey, setMaskAnnotatorKey] = useState(0);
  const [labelDecisions, setLabelDecisions] = useState(new Map());
  const [isCompleting, setIsCompleting] = useState(false);
  
  const lockUpdateIntervalRef = useRef(null);

  useEffect(() => {
    if (lockId && isOpen) {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }

      lockUpdateIntervalRef.current = setInterval(async () => {
        try {
          const currentLabelNumber = getCurrentLabelNumber();
          const totalLabels = getTotalLabels();
          const progressPercentage = Math.round((currentLabelNumber / totalLabels) * 100);
          
          const response = await authenticatedFetch(
            `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/lock/${lockId}/update-status`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                status_message: `Reviewing label ${currentLabelNumber} of ${totalLabels}`,
                progress_percentage: progressPercentage
              })
            }
          );

          if (!response.ok) {
            console.error('Failed to update lock status');
          } else {
            console.log('Lock status updated successfully');
          }
        } catch (err) {
          console.error('Error updating lock status:', err);
        }
      }, 5000);

      return () => {
        if (lockUpdateIntervalRef.current) {
          clearInterval(lockUpdateIntervalRef.current);
        }
      };
    }

    return () => {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }
    };
  }, [lockId, isOpen, authenticatedFetch, currentLabelIndex, currentJobIndex, recordData]);

  useEffect(() => {
    if (isOpen && recordId) {
      setLoading(false);
      setError(null);
      setRecordData(null);
      setCurrentLabelIndex(0);
      setCurrentJobIndex(0);
      setLockId(null);
      setMaskAnnotatorKey(0);
      setLabelDecisions(new Map());
      setIsCompleting(false);
      
      acquireLockAndFetchData();
    }
    
    return () => {
      if (lockUpdateIntervalRef.current) {
        clearInterval(lockUpdateIntervalRef.current);
      }
    };
  }, [isOpen, recordId]);

  const acquireLockAndFetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const detailResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/${recordId}`
      );

      if (!detailResponse.ok) {
        throw new Error(`HTTP error! status: ${detailResponse.status}`);
      }

      const data = await detailResponse.json();
      const isAlreadyCompleted = data.status === 'completed';
      
      if (!isAlreadyCompleted) {
        const lockResponse = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/${recordId}/lock`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!lockResponse.ok) {
          const errorData = await lockResponse.json();
          throw new Error(errorData.detail || `Failed to lock node: ${lockResponse.status}`);
        }

        const lockData = await lockResponse.json();
        setLockId(lockData.lock_id);
        
        if (onLockAcquired) {
          onLockAcquired();
        }
      } else {
        setLockId(null);
      }

      setRecordData(data);
      setCurrentLabelIndex(0);
      setCurrentJobIndex(0);
    } catch (err) {
      console.error('Error acquiring lock or fetching record detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMaskAnnotatorComplete = (result) => {
    console.log('MaskAnnotator completed for label:', result);
    
    const labelId = result.review_id;
    setLabelDecisions(prev => {
      const newMap = new Map(prev);
      newMap.set(labelId, {
        label_id: labelId,
        accepted: result.status === 'accepted',
        updated_mask: result.status === 'rejected' ? result.masks_data.mask : null,
        comment: result.comment
      });
      return newMap;
    });
    
    const totalLabels = getTotalLabels();
    const reviewedCount = labelDecisions.size + 1;
    
    if (reviewedCount >= totalLabels) {
      completeLockWithDecisions(result);
    } else {
      goToNextLabel();
    }
  };

  const goToNextLabel = () => {
    if (!recordData) return;

    const currentJob = recordData.label_job_id_list[currentJobIndex];
    if (!currentJob) return;

    if (currentLabelIndex < currentJob.labels.length - 1) {
      setCurrentLabelIndex(currentLabelIndex + 1);
      setMaskAnnotatorKey(prev => prev + 1);
    } 
    else if (currentJobIndex < recordData.label_job_id_list.length - 1) {
      setCurrentJobIndex(currentJobIndex + 1);
      setCurrentLabelIndex(0);
      setMaskAnnotatorKey(prev => prev + 1);
    }
    else {
      console.log('All labels reviewed!');
    }
  };

  const goToPreviousLabel = () => {
    if (!recordData) return;

    if (currentLabelIndex > 0) {
      setCurrentLabelIndex(currentLabelIndex - 1);
      setMaskAnnotatorKey(prev => prev + 1);
    }
    else if (currentJobIndex > 0) {
      setCurrentJobIndex(currentJobIndex - 1);
      const previousJob = recordData.label_job_id_list[currentJobIndex - 1];
      setCurrentLabelIndex(previousJob.labels.length - 1);
      setMaskAnnotatorKey(prev => prev + 1);
    }
  };

  const handleReleaseLock = async () => {
    if (!recordId) return;
    
    if (lockUpdateIntervalRef.current) {
      clearInterval(lockUpdateIntervalRef.current);
    }
    
    if (lockId) {
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/${recordId}/release-lock`,
          {
            method: 'POST'
          }
        );

        if (!response.ok) {
          throw new Error('Failed to release lock');
        }
      } catch (err) {
        console.error('Error releasing lock:', err);
      }
    }
    
    onClose();
  };

  const completeLockWithDecisions = async (lastResult) => {
    if (!recordData || isCompleting) return;
    
    if (lockUpdateIntervalRef.current) {
      clearInterval(lockUpdateIntervalRef.current);
    }
    
    setIsCompleting(true);
    
    try {
      const allDecisions = new Map(labelDecisions);
      allDecisions.set(lastResult.review_id, {
        label_id: lastResult.review_id,
        accepted: lastResult.status === 'accepted',
        updated_mask: lastResult.status === 'rejected' ? lastResult.masks_data.mask : null,
        comment: lastResult.comment
      });
      
      const labelJobUpdates = [];
      
      for (const job of recordData.label_job_id_list) {
        const jobLabels = [];
        
        for (const label of job.labels) {
          const decision = allDecisions.get(label.label_id);
          
          if (decision) {
            jobLabels.push({
              label_id: decision.label_id,
              accepted: decision.accepted,
              updated_mask: decision.updated_mask
            });
          }
        }
        
        if (jobLabels.length > 0) {
          labelJobUpdates.push({
            label_job_id: job.label_job_id,
            labels: jobLabels
          });
        }
      }
      
      if (lockId) {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/complete-lock`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              node_id: recordId,
              label_jobs: labelJobUpdates
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to complete lock');
        }

        console.log('Lock completed successfully');
      } else {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/update-completed`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              node_id: recordId,
              label_jobs: labelJobUpdates
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to update completed record');
        }

        console.log('Completed record updated successfully');
      }
      
      onClose();
      
    } catch (err) {
      console.error('Error completing/updating record:', err);
      setError(err.message);
      setIsCompleting(false);
    }
  };

  const getCurrentLabel = () => {
    if (!recordData || !recordData.label_job_id_list[currentJobIndex]) return null;
    const currentJob = recordData.label_job_id_list[currentJobIndex];
    return currentJob.labels[currentLabelIndex];
  };

  const getTotalLabels = () => {
    if (!recordData) return 0;
    return recordData.label_job_id_list.reduce((sum, job) => sum + job.labels.length, 0);
  };

  const getCurrentLabelNumber = () => {
    if (!recordData) return 0;
    let count = 0;
    for (let i = 0; i < currentJobIndex; i++) {
      count += recordData.label_job_id_list[i].labels.length;
    }
    count += currentLabelIndex + 1;
    return count;
  };

  const parseMaskData = (segmentationData) => {
    if (!segmentationData) return null;
    
    try {
      const parsed = typeof segmentationData === 'string' 
        ? JSON.parse(segmentationData) 
        : segmentationData;
      
      return {
        mask: parsed.mask,
        tag: '',
        comment: ''
      };
    } catch (err) {
      console.error('Error parsing mask data:', err);
      return null;
    }
  };

  if (!isOpen) return null;

  const currentLabel = getCurrentLabel();
  const totalLabels = getTotalLabels();
  const currentNumber = getCurrentLabelNumber();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleReleaseLock}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              title="Release lock and close"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold">Segmentation Verification Review</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {loading ? 'Loading...' : `Label ${currentNumber} of ${totalLabels}`}
                {lockId && <span className="ml-2 text-xs opacity-75">(Lock active - auto-updating)</span>}
              </p>
            </div>
          </div>
          <button
            onClick={handleReleaseLock}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            title="Release lock and close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {loading || isCompleting ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">
                  {isCompleting ? 'Completing review...' : 'Loading...'}
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <p className="text-red-600 font-semibold mb-2">Error loading record</p>
                <p className="text-gray-600">{error}</p>
                <button
                  onClick={handleReleaseLock}
                  className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          ) : currentLabel ? (
            <>
              <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {currentLabel.tag || 'No tag'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Kind: <span className="font-medium">{currentLabel.label_kind}</span>
                  </div>
                  {currentLabel.confidence && (
                    <div className="text-sm text-gray-600">
                      Confidence: <span className="font-medium">{(currentLabel.confidence * 100).toFixed(1)}%</span>
                    </div>
                  )}
                  <div className="text-sm text-indigo-600 font-medium">
                    Reviewed: {labelDecisions.size} / {totalLabels}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToPreviousLabel}
                    disabled={currentJobIndex === 0 && currentLabelIndex === 0}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={goToNextLabel}
                    disabled={currentJobIndex === recordData.label_job_id_list.length - 1 && 
                              currentLabelIndex === recordData.label_job_id_list[currentJobIndex].labels.length - 1}
                    className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                <MaskAnnotator
                  key={maskAnnotatorKey}
                  imageUrl={currentLabel.image_url}
                  maskData={parseMaskData(currentLabel.segmentation_data)}
                  tag={currentLabel.tag}
                  tagColor="#4F46E5"
                  review_lock_id={recordData.record_identifier}
                  review_id={currentLabel.label_id}
                  onComplete={handleMaskAnnotatorComplete}
                  containerMode={true}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-600">No labels to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SegmentationVerificationTable() {
  const { authenticatedFetch } = useAuth();
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter States
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingRow, setReportingRow] = useState(null);
  const [reportType, setReportType] = useState('');
  const [actualLensCount, setActualLensCount] = useState('');

  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const pageSize = 20;
  const refreshIntervalRef = useRef(null);

  const handleReportProblem = async () => {
    if (!reportType || (reportType === 'wrong_count' && !actualLensCount)) {
      alert('Please select a problem type and fill in required fields');
      return;
    }

    try {
      let endpoint;
      let requestBody;

      if (reportType === 'wrong_count') {
        endpoint = `${config.BACKEND_API_ADDRESS}/revision/lenses/wrong-count`;
        requestBody = {
          node_id: reportingRow.id,
          correct_lens_count: parseInt(actualLensCount)
        };
      } else if (reportType === 'duplicate') {
        endpoint = `${config.BACKEND_API_ADDRESS}/revision/lenses/duplicate`;
        requestBody = {
          node_id: reportingRow.id
        };
      } else {
        alert('Invalid problem type selected');
        return;
      }

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Problem reported successfully: ${result.message}`);
        setReportModalOpen(false);
        setReportingRow(null);
        setReportType('');
        setActualLensCount('');
        handleRefresh();
      } else {
        const errorData = await response.json();
        alert(`Failed to report problem: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error reporting problem:', error);
      alert('Error reporting problem');
    }
  };

  const fetchRecords = useCallback(async (page, filter = statusFilter, search = searchQuery, sort = sortOrder) => {
    setLoading(true);
    setError(null);

    try {
      let url = `${config.BACKEND_API_ADDRESS}/nodes/segmentation-verification/list?page=${page}&page_size=${pageSize}&sort_order=${sort}`;
      
      if (filter && filter !== 'all') {
        url += `&status=${filter}`;
      }

      if (search && search.trim() !== '') {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      setRecords(data.items);
      setTotalCount(data.total);
      setTotalPages(data.total_pages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching records:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, statusFilter, searchQuery, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
    fetchRecords(1, statusFilter, searchQuery, sortOrder);
  }, [statusFilter, searchQuery, sortOrder]);

  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      fetchRecords(currentPage, statusFilter, searchQuery, sortOrder);
    }, 5000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchRecords, statusFilter, searchQuery, sortOrder, currentPage]);

  // Keyboard shortcut to open first available record
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in input fields or if modal is already open
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || detailModalOpen || reportModalOpen) {
        return;
      }

      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        // Find first non-locked record
        const firstAvailableRecord = records.find(record => record.status !== 'running');
        if (firstAvailableRecord) {
          handleViewRecordDetails(firstAvailableRecord);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [records, detailModalOpen, reportModalOpen]);

  const handleRefresh = () => {
    fetchRecords(currentPage, statusFilter, searchQuery, sortOrder);
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput);
  };

  const handleViewRecordDetails = (record) => {
    if (record.status === 'running') {
      return;
    }
    
    setSelectedRecordId(record.segmentation_node_id);
    setDetailModalOpen(true);
  };

  const handleModalClose = () => {
    setDetailModalOpen(false);
    setSelectedRecordId(null);
    handleRefresh();
  };

  const handleLockAcquired = () => {
    fetchRecords(currentPage, statusFilter, searchQuery, sortOrder);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchRecords(currentPage - 1, statusFilter, searchQuery, sortOrder);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchRecords(currentPage + 1, statusFilter, searchQuery, sortOrder);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Segmentation Verification
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total records
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
          
          {/* Controls Bar: Search, Sort, Filter */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            
            <div className="flex items-center gap-3 flex-1 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1 md:max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  onBlur={handleSearchSubmit}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 sm:text-sm transition-colors"
                  placeholder="Search by Workflow ID or Name..."
                />
              </div>
              
              {/* Sort Order */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ArrowUpDown className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg leading-5 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 sm:text-sm appearance-none cursor-pointer hover:bg-gray-50"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 w-full md:w-auto">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap flex items-center gap-1">
                <Filter className="w-4 h-4" /> Filter:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleStatusFilterChange('all')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors whitespace-nowrap ${
                    statusFilter === 'all'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleStatusFilterChange('pending')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors whitespace-nowrap ${
                    statusFilter === 'pending'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Pending
                </button>
                <button
                  onClick={() => handleStatusFilterChange('running')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors whitespace-nowrap ${
                    statusFilter === 'running'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Running
                </button>
                <button
                  onClick={() => handleStatusFilterChange('completed')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors whitespace-nowrap ${
                    statusFilter === 'completed'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading records</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {records.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Layers className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  No records found
                </h3>
                <p className="text-gray-600">
                  No segmentation verification records available
                  {statusFilter !== 'all' && ` with status "${statusFilter}"`}
                  {searchQuery && ` matching "${searchQuery}"`}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {records.map((record) => {
                  const isLocked = record.status === 'running';
                  const totalLabels = record.label_jobs.reduce(
                    (sum, job) => sum + job.label_count, 
                    0
                  );

                  return (
                    <div 
                      key={record.segmentation_node_id}
                      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
                        isLocked ? 'opacity-75' : ''
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Images Section */}
                          <div className="flex-shrink-0">
                            {(() => {
                              const allImages = record.label_jobs.flatMap(job => job.image_urls || []);
                              const displayImages = allImages.slice(0, 4);
                              const remainingCount = allImages.length - displayImages.length;
                              
                              return (
                                <div className="flex gap-2">
                                  {displayImages.length > 0 ? (
                                    <>
                                      {displayImages.map((imageUrl, imgIndex) => (
                                        <div 
                                          key={imgIndex}
                                          className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-indigo-400 transition-colors flex-shrink-0"
                                        >
                                          <img 
                                            src={imageUrl} 
                                            alt={`Preview ${imgIndex + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      ))}
                                      {remainingCount > 0 && (
                                        <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                          <span className="text-xs font-medium text-gray-600">
                                            +{remainingCount}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                      <Image className="w-8 h-8 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          
                          {/* Info Section */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-sm font-semibold text-gray-900">
                                Workflow: 
                              </span>
                              <span className="text-sm text-gray-700 font-medium">
                                {record.workflow_name || 'Unnamed Workflow'}
                              </span>
                              <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200 text-blue-700 font-mono">
                                {record.workflow_id || 'N/A'}
                              </code>
                            </div>
                            
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-sm font-semibold text-gray-900">
                                Node: 
                              </span>
                              <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                {record.segmentation_node_id}
                              </code>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm">
                              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                                {getStatusIcon(record.status)}
                                {record.status}
                                {isLocked && <Lock className="w-3 h-3 ml-1" />}
                              </span>
                              
                              <span className="text-gray-600">
                                Jobs: <span className="font-medium">{record.label_jobs.length}</span>
                              </span>
                              
                              <span className="text-gray-600">
                                Labels: <span className="font-medium">{totalLabels}</span>
                              </span>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex-shrink-0 flex gap-2">
                            <button
                              onClick={() => {
                                const allImages = record.label_jobs.flatMap(job => job.image_urls || []);
                                setReportingRow({
                                  id: record.segmentation_node_id,
                                  workflowId: record.workflow_id,
                                  images: allImages,
                                  record: record
                                });
                                setReportModalOpen(true);
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                            >
                              <AlertCircle className="w-4 h-4" />
                              Report Problem
                            </button>
                            
                            <button
                              onClick={() => handleViewRecordDetails(record)}
                              disabled={isLocked}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                                isLocked
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : record.status === 'completed'
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
                              }`}
                              title={
                                isLocked 
                                  ? 'This node is currently locked by another user' 
                                  : record.status === 'completed'
                                  ? 'Re-review this completed record'
                                  : 'Review all labels'
                              }
                            >
                              {isLocked ? (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Locked
                                </>
                              ) : record.status === 'completed' ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  Re-Review
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4" />
                                  Review All Labels
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              <div className="mt-6 flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-600">
                  Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                  {' '} ({totalCount} total records)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || loading}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || loading}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Record Detail Modal with MaskAnnotator */}
      <RecordDetailModal
        isOpen={detailModalOpen}
        onClose={handleModalClose}
        recordId={selectedRecordId}
        onLockAcquired={handleLockAcquired}
      />
      
      {/* Report Problem Modal */}
      {reportModalOpen && reportingRow && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Report Problem
              </h3>
              
              {/* Display Images */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                {reportingRow.images.length > 0 ? (
                  reportingRow.images.map((img, idx) => (
                    <div key={idx} className="border rounded p-2">
                      <img 
                        src={img} 
                        alt={`Label ${idx + 1}`} 
                        className="w-full h-48 object-contain rounded"
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center text-gray-500 py-8">
                    No images available
                  </div>
                )}
              </div>

              {/* Problem Type Buttons */}
              {!reportType ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Problem Type:
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setReportType('duplicate')}
                      className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Duplicate Lenses
                    </button>
                    <button
                      onClick={() => setReportType('wrong_count')}
                      className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      Wrong Number of Lenses
                    </button>
                  </div>
                </div>
              ) : reportType === 'duplicate' ? (
                <div className="mb-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <p className="text-sm font-medium text-red-800">
                      You selected: Duplicate Lenses
                    </p>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setReportType('')}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleReportProblem}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Confirm & Submit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                    <p className="text-sm font-medium text-orange-800 mb-3">
                      You selected: Wrong Number of Lenses
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specify actual number of lenses:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={actualLensCount}
                      onChange={(e) => setActualLensCount(e.target.value)}
                      className="border rounded px-3 py-2 w-full"
                      placeholder="Enter actual count"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setReportType('');
                        setActualLensCount('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleReportProblem}
                      disabled={!actualLensCount}
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm & Submit
                    </button>
                  </div>
                </div>
              )}

              {/* Close Button (only shown when no problem type selected) */}
              {!reportType && (
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setReportModalOpen(false);
                      setReportingRow(null);
                      setReportType('');
                      setActualLensCount('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SegmentationVerificationTable;